<div
  class="tw-flex tw-flex-col"
  [ngClass]="useRedesign ? 'tw-gap-4 tw-pb-6' : 'tw-gap-8'"
  *ngIf="pluginConfig"
>
  <!-- Legacy header: link illustration between the app and the plugin logo -->
  <div *ngIf="!useRedesign" class="tw-flex tw-w-full tw-gap-1 tw-justify-center">
    <div class="tw-flex tw-items-center tw-justify-center tw-p-4 tw-rounded-full tw-bg-blue-300">
      <icon [name]="'links'" [size]="8"></icon>
    </div>
    <div
      class="tw-flex tw-whitespace-nowrap tw-justify-center tw-items-center tw-w-full tw-max-w-32"
    >
      <div class="tw-flex tw-w-3 tw-h-3 tw-shadow-inner tw-rounded-full tw-bg-blue-300"></div>
      <div class="tw-flex tw-w-full tw-h-1 tw-bg-blue-300"></div>
      <div class="tw-flex tw-w-3 tw-h-3 tw-shadow-inner tw-rounded-full"></div>
    </div>
    <div
      class="tw-flex tw-items-center tw-justify-center tw-p-4 tw-rounded-full tw-bg-dt-surface-card"
    >
      <img *ngIf="pluginImage" [src]="pluginImage" [alt]="pluginConfig.name" class="tw-h-8" />
    </div>
  </div>

  <!-- Redesigned header: logo tile, name, kind, status chip (DS 2.1 Manage panel) -->
  <div
    *ngIf="useRedesign && !embedded"
    class="tw-flex tw-items-center tw-gap-3.5"
    data-testid="plugin-connect-header"
  >
    <span
      class="tw-flex tw-items-center tw-justify-center tw-size-11 tw-shrink-0 tw-overflow-hidden tw-rounded-[14px] tw-bg-color-surface-sunken tw-text-sm tw-font-semibold tw-text-color-text-secondary"
    >
      <img
        *ngIf="pluginImage"
        [src]="pluginImage"
        [alt]="pluginMetadata?.displayName || pluginConfig.name"
        class="tw-h-7 tw-max-w-[36px] tw-object-contain"
      />
    </span>
    <span class="tw-flex tw-flex-col tw-flex-1 tw-min-w-0">
      <span
        class="tw-truncate tw-text-lg tw-font-semibold tw-leading-6 tw-text-color-text-default"
        >{{ pluginMetadata?.displayName || pluginConfig.name }}</span
      >
      <span class="tw-text-xs tw-font-medium tw-text-color-text-tertiary">{{
        "pluginConnectSubtitle" | i18n
      }}</span>
    </span>
    <span
      *ngIf="!forceReauth && !syncOnly"
      class="tw-flex tw-items-center tw-h-7 tw-px-3 tw-rounded-full tw-bg-color-surface-sunken tw-text-xs tw-font-semibold tw-text-color-text-secondary"
      >{{ "pluginConnectNotConnected" | i18n }}</span
    >
  </div>

  <!-- Australian Super: credentials → OTP → sync -->
  <ng-container *ngIf="isSuperPlugin(); else legacyPluginFlow">
    <p *ngIf="authErrorMessage" class="tw-text-red-600 tw-text-sm tw-text-center" role="alert">
      {{ authErrorMessage }}
    </p>

    <div *ngIf="authPhase === 'credentials'">
      <ng-container *ngTemplateOutlet="credentialsForm"></ng-container>
    </div>

    <div
      *ngIf="authPhase === 'authenticating'"
      class="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-py-8"
    >
      <div
        class="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-0 tw-border-b-2 tw-border-blue-500"
      ></div>
      <span class="tw-text-dt-text-secondary">{{ "signingInToAustralianSuper" | i18n }}</span>
    </div>

    <div *ngIf="authPhase === 'otp'" class="tw-flex tw-flex-col tw-gap-4">
      <p class="tw-text-dt-text-secondary tw-text-center">
        {{ "enterVerificationCodeSentToDevice" | i18n }}
      </p>
      <form [formGroup]="otpForm" (ngSubmit)="submitOtp()">
        <app-input
          [label]="'verificationCode' | i18n"
          [formControl]="$any(otpForm.get('otp'))"
          type="text"
          [placeholder]="'sixDigitCode' | i18n"
        ></app-input>
        <modal-footer>
          <div class="tw-flex tw-flex-col tw-gap-2 tw-w-full">
            <app-gloss-button class="tw-w-full" [options]="verifyOtpButton"></app-gloss-button>
            <app-gloss-button class="tw-w-full" [options]="resendOtpButton"></app-gloss-button>
          </div>
        </modal-footer>
      </form>
    </div>

    <div
      *ngIf="authPhase === 'syncing'"
      class="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-py-8"
    >
      <div
        class="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-0 tw-border-b-2 tw-border-blue-500"
      ></div>
      <span class="tw-text-dt-text-secondary">{{ "syncingYourData" | i18n }}</span>
    </div>
  </ng-container>

  <!-- Opal and other plugins: legacy form / auto-sync update -->
  <ng-template #legacyPluginFlow>
    <div *ngIf="!isUpdate">
      <p
        *ngIf="authErrorMessage"
        class="tw-text-dt-text-error tw-text-sm tw-text-center tw-mb-4"
        role="alert"
      >
        {{ authErrorMessage }}
      </p>
      <ng-container *ngTemplateOutlet="credentialsForm"></ng-container>
    </div>

    <div *ngIf="isUpdate" class="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-py-8">
      <div
        class="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-0 tw-border-b-2 tw-border-blue-500"
      ></div>
      <span class="tw-text-dt-text-secondary">Syncing your data...</span>
    </div>
  </ng-template>
  <!-- Credentials form: legacy by default, redesigned when `redesigned` / `embedded` is set -->
  <ng-template #credentialsForm>
    <ng-container *ngIf="useRedesign; else legacyCredentialsForm">
      <form
        [formGroup]="configForm"
        (ngSubmit)="onSubmit($event)"
        (input)="showIncompleteHint = false"
      >
        <div
          class="tw-flex tw-flex-col tw-gap-3 tw-text-[13px] tw-leading-[1.5] tw-text-color-text-secondary"
          [ngClass]="
            embedded
              ? ''
              : 'tw-p-4 tw-rounded-2xl tw-bg-white/45 tw-border tw-border-solid tw-border-white/40'
          "
          data-testid="plugin-connect-card"
        >
          <ng-container *ngIf="!embedded">
            <span
              class="tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-[0.08em] tw-text-color-text-tertiary"
              >{{ "pluginConnectSignIn" | i18n }}</span
            >
            <span>{{
              "pluginConnectHint" | i18n: pluginMetadata?.displayName || pluginConfig.name
            }}</span>
          </ng-container>
          <div class="tw-flex tw-flex-col tw-gap-4">
            <app-input
              *ngFor="let field of pluginConfig.fields"
              [label]="field.label"
              [formControl]="$any(configForm.get(field.field))"
              [type]="getFieldType(field.type)"
              [placeholder]="field.label || ''"
            ></app-input>
          </div>
          <span
            *ngIf="showIncompleteHint"
            class="tw-text-xs tw-font-medium tw-text-color-status-error-text"
            role="alert"
            data-testid="plugin-connect-incomplete"
            >{{ "pluginConnectIncomplete" | i18n }}</span
          >
          <div>
            <button
              type="submit"
              class="tw-h-10 tw-px-5 tw-rounded-xl tw-bg-color-intent-brand tw-text-sm tw-font-semibold tw-text-color-text-on-action tw-shadow-elevation-control disabled:tw-opacity-60"
              [disabled]="!nextButton.isEnabled"
              data-testid="plugin-connect-submit"
            >
              {{ submitLabel || nextButton.text }}
            </button>
          </div>
        </div>
      </form>
    </ng-container>
  </ng-template>

  <ng-template #legacyCredentialsForm>
    <form [formGroup]="configForm" (ngSubmit)="onSubmit($event)">
      <div class="tw-flex tw-flex-col tw-gap-4">
        <app-input
          *ngFor="let field of pluginConfig.fields"
          [label]="field.label"
          [formControl]="$any(configForm.get(field.field))"
          [type]="getFieldType(field.type)"
          [placeholder]="field.label || ''"
        ></app-input>
      </div>
      <modal-footer>
        <app-gloss-button class="tw-w-full" [options]="nextButton"></app-gloss-button>
      </modal-footer>
    </form>
  </ng-template>
</div>

<div *ngIf="!pluginConfig">
  <p>Plugin configuration not found</p>
</div>
